/**
 * The drag the non-table views share — **dnd-kit**, the library ADR-001 names.
 *
 * Round 5u used the HTML5 drag API (decision D-V2-2) because dnd-kit was not a dependency of
 * `apps/web` and adding one mid-round would have touched a lockfile two other workers shared.
 * Round 5x adds it (`@dnd-kit/core` 6.3.1, `@dnd-kit/sortable` 10.0.0) and the two gestures
 * move over. Three things come with it that the native API cannot give:
 *
 * 1. **A keyboard alternative** (open question q405/q414). `KeyboardSensor` starts a drag on
 *    <kbd>Space</kbd>, moves with the arrow keys and drops on <kbd>Space</kbd>, so a kanban
 *    card and a calendar pill can be moved without a pointer. The native drag API has none.
 * 2. **A real drag preview** — `<DragOverlay>` draws the card itself, not the browser's
 *    screenshot of it, which is what monday shows.
 * 3. A drag that Playwright can actually perform: the native API never fires from a synthetic
 *    mouse, so the round-5u tests had to *dispatch* `DragEvent`s and could only ever prove the
 *    handler, never the gesture. Pointer events are real input.
 *
 * The arithmetic is untouched: `kanban/lanes.ts` still says what a drop writes.
 */
import {
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
  type CollisionDetection,
  type KeyboardCoordinateGetter,
} from '@dnd-kit/core';

/**
 * `distance: 5` is the whole reason a card is still clickable: a card's title is a button and
 * a press that never moves must reach it, so the drag only starts once the pointer has
 * travelled five pixels (`[V2§9 C4]` opens the item card on a plain click).
 *
 * **P-142.** `coordinateGetter` is the half the keyboard path was missing. dnd-kit's default
 * getter moves the drag by 25 px per arrow press, which reaches the next **lane** on no board
 * this product draws (a lane is 280 px wide), so Space-Right-Right-Space left the card exactly
 * where it was: no `over`, no drop, no cell write. A kind that has a keyboard path passes its
 * own getter; one that does not keeps the default and is no worse than it was.
 */
export function useViewDragSensors(coordinateGetter?: KeyboardCoordinateGetter) {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, coordinateGetter === undefined ? {} : { coordinateGetter }),
  );
}

/**
 * **P-142.** `pointerWithin` is the right algorithm for a pointer drag — it is what makes a drop
 * land in the lane the cursor is actually over rather than the nearest one — and it is *only*
 * that: it returns nothing at all when there are no pointer coordinates, which is every keyboard
 * drag. So the keyboard sensor moved the drag and `onDragEnd` still saw `over === null`.
 *
 * This is dnd-kit's own documented answer: the pointer algorithm while there is a pointer, a
 * rectangle algorithm otherwise.
 */
export const viewCollisionDetection: CollisionDetection = (args) =>
  args.pointerCoordinates === null ? closestCenter(args) : pointerWithin(args);

/**
 * **P-142.** The keyboard coordinate getter for a **lane strip**: ← / → step to the previous or
 * next lane, ↑ / ↓ do nothing, because the kanban never reorders within a lane (a lane is a
 * pivot bucket, not a group — `[V2§7.3]`).
 *
 * It returns the coordinates that put the dragged card's collision rectangle over the middle of
 * the target lane, which is what the collision detection above then resolves to that lane's
 * droppable — the same `event.over` a pointer drop produces, so the write path is shared.
 */
export const laneKeyboardCoordinates: KeyboardCoordinateGetter = (
  event,
  { currentCoordinates, context },
) => {
  const step = event.code === 'ArrowRight' ? 1 : event.code === 'ArrowLeft' ? -1 : 0;
  if (step === 0) return undefined;

  const { collisionRect, droppableContainers, droppableRects } = context;
  if (collisionRect === null) return undefined;

  // The lanes in painted order, which is their own left-to-right order on screen.
  const lanes = droppableContainers
    .getEnabled()
    .map((container) => ({ id: container.id, rect: droppableRects.get(container.id) }))
    .flatMap((lane) => (lane.rect === undefined ? [] : [{ id: lane.id, rect: lane.rect }]))
    .sort((a, b) => a.rect.left - b.rect.left);
  if (lanes.length === 0) return undefined;

  // Where the card is now: the lane whose centre is nearest the card's own centre.
  const centre = collisionRect.left + collisionRect.width / 2;
  let index = 0;
  let best = Number.POSITIVE_INFINITY;
  for (let at = 0; at < lanes.length; at += 1) {
    const lane = lanes[at]!;
    const distance = Math.abs(lane.rect.left + lane.rect.width / 2 - centre);
    if (distance < best) {
      best = distance;
      index = at;
    }
  }

  const target = lanes[Math.min(Math.max(index + step, 0), lanes.length - 1)];
  if (target === undefined || target === lanes[index]) return undefined;
  return {
    x: target.rect.left + (target.rect.width - collisionRect.width) / 2,
    y: currentCoordinates.y,
  };
};
